import React, { useEffect, useState } from 'react'
import styles from './ProfileCompletionForm.module.css'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import {
    EmptyProfileWizardState,
    ProfileWizardAction,
} from '../../Store/Actions/ProfileAction'
import {
    ADD_ADDRESS_SUCCESS,
    addAddress,
} from '../../Store/Actions/AddressActions'
import useAlert from '../../Hooks/useAlert/useAlert'
import InputBox from '../../Components/InputBox/InputBox'
import { ButtonCmp, DropDownCmp, WhiteButtonCmp } from '../../Components'
import { useNavigate } from 'react-router-dom'
import { postMethodCustomHeader } from '../../Utils/response'
import { GetCitiesAction } from '../../Store/Actions/GetActions'

const billingAddressSchema = yup.object().shape({
    billing_title: yup.string().required('Title is required'),
    billing_street_address: yup.string().required('Street Address is required'),
    billing_state_id: yup
        .object()
        .shape({
            label: yup.string(),
            id: yup.number(),
        })
        .nullable()
        .required('State is required'),
    billing_city_id: yup
        .object()
        .shape({
            label: yup.string(),
            id: yup.number(),
        })
        .nullable()
        .required('City is required'),
    billing_type: yup
        .object({
            label: yup.string(),
            value: yup.string(),
        })
        .nullable()
        .required('Type is required'),
    billing_postal_code: yup
        .string()
        .min(3, 'Postal Code must be at least 3 characters')
        .required('Postal Code is required'),
})

const shippingAddressSchema = yup.object().shape({
    shipping_title: yup.string().required('Title is required'),
    shipping_street_address: yup
        .string()
        .required('Street Address is required'),
    shipping_state_id: yup
        .object()
        .shape({
            label: yup.string(),
            id: yup.number(),
        })
        .nullable()
        .required('State is required'),
    shipping_city_id: yup
        .object()
        .shape({
            label: yup.string(),
            id: yup.number(),
        })
        .nullable()
        .required('City is required'),
    shipping_type: yup
        .object({
            label: yup.string(),
            value: yup.string(),
        })
        .nullable()
        .required('Type is required'),
    shipping_postal_code: yup
        .string()
        .min(3, 'Postal Code must be at least 3 characters')
        .required('Postal Code is required'),
})

const typeOptions = [
    { label: 'Shipping', value: 'Shipping' },
    // { label: 'General', value: 'General' },
    { label: 'Billing', value: 'Billing' },
    { label: 'PickUp', value: 'PickUp' },
]
const AddressForm = ({
    setWhichForm,
    setBarPercent,
    setAddressComplete,
    profileDetails,
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { setAlert } = useAlert()
    const statesData = useSelector(
        (state) => state.StatesReducers.statesdata || []
    )
    const { citiesdata, citiesLoading } = useSelector(
        (state) => state.CitiesReducers
    )
    const { addresses, loading } = useSelector((state) => state.AddressReducer)

    const [isBillingSelected, setIsBillingSelected] = useState(false)

    const { profileWizarddata } = useSelector(
        (state) => state.ProfielWizardReducer
    )
    const {
        control: billingControl,
        watch: billingWatch,
        formState: { errors: billingErrors },
    } = useForm({
        defaultValues: {
            billing_title: '',
            billing_street_address: '',
            billing_city_id: null,
            billing_state_id: null,
            billing_type: '',
            billing_postal_code: '',
        },
        resolver: yupResolver(billingAddressSchema),
    })
    const {
        control,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            title: '',
            street_address: '',
            city_id: null,
            state_id: null,
            type: '',
            postal_code: '',
        },
        resolver: yupResolver(shippingAddressSchema),
    })

    const initialData = null

    const usedTypes = addresses
        ? addresses
              .filter((addr) => !initialData || addr.id !== initialData.id)
              .map((addr) => addr.type)
        : []
    const filteredTypeOptions = typeOptions.filter(
        (opt) =>
            !usedTypes.includes(opt.value) ||
            (initialData && initialData.type === opt.value)
    )

    const onSubmit = async (type) => {
        const data = { ...(type === 'Billing' ? billingWatch() : watch()) }
        const apiData = {
            title: type === 'Billing' ? data?.billing_title : data?.title,
            type: type,
            city_id:
                type === 'Billing'
                    ? data.billing_city_id?.id || data.billing_city_id
                    : data?.city_id?.id || data?.city_id,
            state_id:
                type === 'Billing'
                    ? data.billing_state_id?.id || data.billing_state_id
                    : data?.state_id?.id || data?.state_id,
            street_address:
                type === 'Billing'
                    ? data.billing_street_address
                    : data?.street_address,
            postal_code:
                type === 'Billing'
                    ? data.billing_postal_code
                    : data?.postal_code,
            // type: data.type?.value || data.type,
        }
        // // dispatch(addAddress(apiData, setAlert))

        const response = await postMethodCustomHeader(`user/address`, apiData)
        if (response.data.success) {
            setAlert(response.data?.message, 'success')
            dispatch({
                type: ADD_ADDRESS_SUCCESS,
                payload: response.data.data,
            })
        } else {
            setAlert(response.data?.message, 'error')
        }
    }

    const moveNext = () => {
        setWhichForm('services')
        setBarPercent(`${(100 / 5) * 2}%`)
        setAddressComplete(true)
    }

    const billingState = watch('billing_state_id', false)

    useEffect(() => {
        if (billingState) {
            dispatch(GetCitiesAction(billingState.label))
        }
    }, [billingState])

    const stateState = watch('state_id', false)

    useEffect(() => {
        if (stateState) {
            dispatch(GetCitiesAction(stateState.label))
        }
    }, [stateState])

    useEffect(() => {
        if (profileWizarddata) {
            setWhichForm('services')
            setBarPercent(`${(100 / 5) * 2}%`)
            setAddressComplete(true)
            dispatch(EmptyProfileWizardState())
        }
    }, [profileWizarddata])
    return (
        <section className={styles.profileInfo}>
            <div>
                <Typography noWrap variant="h5" fontWeight={'bold'}>
                    Shipping and Billing Address
                </Typography>
                <Typography variant="caption" sx={{ color: '#000000' }}>
                    Kindly ensure that all pickup and delivery addresses are
                    accurate. These addresses will be transmitted to UPS for
                    courier booking, shipment processing, and timely delivery of
                    your product.
                </Typography>
            </div>
            <Box sx={{ my: '30px' }}>
                <Typography fontSize="16px" sx={{ color: 'var(--textColor)' }}>
                    Account Email{' '}
                    <span className="MuiFormLabel-asterisk">*</span>
                </Typography>
                <Typography fontSize="18px" sx={{ mt: '5px' }}>
                    {profileDetails.email
                        ? profileDetails.email
                        : 'taimoorjohn@hotmail.com'}
                </Typography>
            </Box>
            <fieldset>
                <legend className={styles.legend}>Shipping Address</legend>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputBox
                            label="Full Name"
                            name="title"
                            inputProps={{ 'data-testid': 'title' }}
                            error={!!errors.title}
                            helperText={errors.title?.message}
                            required
                            fullWidth
                            control={control}
                            // {...control.register('title')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputBox
                            label="Street Address"
                            name="street_address"
                            inputProps={{ 'data-testid': 'street_address' }}
                            error={!!errors.street_address}
                            helperText={errors.street_address?.message}
                            required
                            fullWidth
                            control={control}
                            // {...control.register('street_address')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <DropDownCmp
                            name="state_id"
                            control={control}
                            label="State"
                            options={statesData}
                            optionLabel="label"
                            optionValue="id"
                            required
                            error={!!errors.state_id}
                            helperText={errors.state_id?.message}
                            fullWidth
                            disableClearable
                            freeSolo={false}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ position: 'relative' }}>
                            <DropDownCmp
                                name="city_id"
                                control={control}
                                label="City"
                                options={citiesdata || []}
                                optionLabel="label"
                                optionValue="id"
                                required
                                error={!!errors.city_id}
                                helperText={errors.city_id?.message}
                                fullWidth
                                disableClearable
                                freeSolo={false}
                            />
                            {citiesLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor:
                                            'rgba(255, 255, 255, 0.8)',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        zIndex: 1,
                                        borderRadius: '4px',
                                    }}
                                >
                                    <CircularProgress size={20} />
                                    <span
                                        style={{
                                            marginLeft: '8px',
                                            fontSize: '12px',
                                        }}
                                    >
                                        Loading cities...
                                    </span>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    {/* <Grid item xs={12} sm={6} md={4}>
                        <DropDownCmp
                            name="type"
                            control={control}
                            label="Type"
                            options={filteredTypeOptions}
                            optionLabel="label"
                            optionValue="value"
                            required
                            error={!!errors.type}
                            helperText={errors.type?.message}
                            fullWidth
                            disableClearable
                            freeSolo={false}
                        />
                    </Grid> */}
                    <Grid item xs={12} sm={6} md={4}>
                        <InputBox
                            label="Postal Code"
                            name="postal_code"
                            inputProps={{ 'data-testid': 'postal_code' }}
                            error={!!errors.postal_code}
                            helperText={errors.postal_code?.message}
                            required
                            fullWidth
                            control={control}
                            // {...control.register('postal_code')}
                        />
                    </Grid>
                </Grid>

                {/* ////btn */}
                <section className={styles.formBtns}>
                    {/* <WhiteButtonCmp title="Cancel" /> */}
                    {/* <WhiteButtonCmp
                        title="Cancel"
                        onClick={() => {
                            navigate('/profile')
                        }}
                    /> */}
                    {/* <ButtonCmp
                        title="Skip Step"
                        onClick={() => {
                            setWhichForm('services')
                        }}
                    /> */}
                    <ButtonCmp
                        title="Save"
                        type="button"
                        onClick={() => {
                            onSubmit('Shipping')
                        }}
                    />
                </section>
            </fieldset>

            <section>
                <label htmlFor="isBilling">
                    <input
                        type="checkbox"
                        name="isbilling"
                        id="isbilling"
                        defaultChecked={isBillingSelected}
                        className={styles.checkbox}
                        onChange={(e) => setIsBillingSelected(e.target.checked)}
                    />
                    <span className={styles.legend}>Add Billing Address</span>
                </label>
            </section>

            {isBillingSelected && (
                <fieldset>
                    <legend className={styles.legend}>Billing Address</legend>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <InputBox
                                label="Full Name"
                                name="billing_title"
                                inputProps={{ 'data-testid': 'title' }}
                                error={!!billingErrors.title}
                                helperText={billingErrors.title?.message}
                                required
                                fullWidth
                                control={billingControl}
                                // {...billingControl.register('title')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InputBox
                                label="Street Address"
                                name="billing_street_address"
                                inputProps={{ 'data-testid': 'street_address' }}
                                error={!!billingErrors.street_address}
                                helperText={
                                    billingErrors.street_address?.message
                                }
                                required
                                fullWidth
                                control={billingControl}
                                // {...billingControl.register('street_address')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <DropDownCmp
                                name="billing_state_id"
                                control={billingControl}
                                label="State"
                                options={statesData}
                                optionLabel="label"
                                optionValue="id"
                                required
                                error={!!billingErrors.state_id}
                                helperText={billingErrors.state_id?.message}
                                fullWidth
                                disableClearable
                                freeSolo={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ position: 'relative' }}>
                                <DropDownCmp
                                    name="billing_city_id"
                                    control={billingControl}
                                    label="City"
                                    options={citiesdata || []}
                                    optionLabel="label"
                                    optionValue="id"
                                    required
                                    error={!!billingErrors.city_id}
                                    helperText={billingErrors.city_id?.message}
                                    fullWidth
                                    disableClearable
                                    freeSolo={false}
                                />
                                {citiesLoading && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor:
                                                'rgba(255, 255, 255, 0.8)',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            zIndex: 1,
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <CircularProgress size={20} />
                                        <span
                                            style={{
                                                marginLeft: '8px',
                                                fontSize: '12px',
                                            }}
                                        >
                                            Loading cities...
                                        </span>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                        {/* <Grid item xs={12} sm={6} md={4}>
                        <DropDownCmp
                            name="billing_type"
                            control={billingControl}
                            label="Type"
                            options={filteredTypeOptions}
                            optionLabel="label"
                            optionValue="value"
                            required
                            error={!!billingErrors.type}
                            helperText={billingErrors.type?.message}
                            fullWidth
                            disableClearable
                            freeSolo={false}
                        />
                    </Grid> */}
                        <Grid item xs={12} sm={6} md={4}>
                            <InputBox
                                label="Postal Code"
                                name="billing_postal_code"
                                inputProps={{ 'data-testid': 'postal_code' }}
                                error={!!billingErrors.postal_code}
                                helperText={billingErrors.postal_code?.message}
                                required
                                fullWidth
                                control={billingControl}
                                // {...control.register('postal_code')}
                            />
                        </Grid>
                    </Grid>

                    {/* ////btn */}
                    <section className={styles.formBtns}>
                        {/* <WhiteButtonCmp title="Cancel" /> */}
                        {/* <WhiteButtonCmp
                        title="Cancel"
                        onClick={() => {
                            navigate('/profile')
                        }}
                    /> */}
                        {/* <ButtonCmp
                        title="Skip Step"
                        onClick={() => {
                            setWhichForm('services')
                        }}
                    /> */}
                        <ButtonCmp
                            title="Save"
                            type="button"
                            onClick={() => {
                                onSubmit('Billing')
                            }}
                        />
                    </section>
                </fieldset>
            )}
            <section className={styles.formBtns}>
                {/* <WhiteButtonCmp title="Cancel" /> */}
                {/* <WhiteButtonCmp
                        title="Cancel"
                        onClick={() => {
                            navigate('/profile')
                        }}
                    /> */}
                <ButtonCmp
                    title="Skip Step"
                    onClick={() => {
                        setWhichForm('services')
                    }}
                />
                <ButtonCmp
                    title="Save and Continue"
                    type="button"
                    onClick={() => {
                        moveNext()
                    }}
                />
            </section>
        </section>
    )
}

export default AddressForm
